import React, {useMemo} from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import makeStyles from "@mui/styles/makeStyles";
import {useTheme} from "@mui/material/styles";
import {Controller} from "react-hook-form";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
	root: {
		"&&, && .MuiFormControl-root": {
			width: "100%",
			minWidth: 0,
		},
		"&&": {
			display: "block",
		},
		"&& .MuiTextField-root, && .MuiAutocomplete-inputRoot": {
			width: "100%",
		},
		"&& .MuiInputBase-root, && .MuiOutlinedInput-root, && .MuiAutocomplete-inputRoot": {
			minHeight: 40,
			borderRadius: "14px !important",
			background: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
			backgroundImage: "none !important",
			backgroundColor: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
			backgroundClip: "padding-box !important",
			backdropFilter: "none !important",
			WebkitBackdropFilter: "none !important",
		},
		"&& .MuiInputBase-input, && .MuiOutlinedInput-input, && .MuiAutocomplete-input": {
			background: "transparent !important",
			backgroundColor: "transparent !important",
		},
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.mode === "light" ? "rgba(32, 50, 64, 0.18)" : "rgba(255, 255, 255, 0.16)",
		},
		"& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: `${theme.palette.error.main} !important`,
			borderWidth: "1px !important",
		},
		"& .MuiInputLabel-root": {
			fontWeight: 800,
		},
		"& .MuiInputLabel-root.Mui-error": {
			color: `${theme.palette.error.main} !important`,
		},
		"& .MuiFormHelperText-root": {
			position: "static !important",
			margin: `${theme.spacing(0.45)} 0 0 ${theme.spacing(1.25)} !important`,
			padding: "0 !important",
			color: `${theme.palette.error.main} !important`,
			background: "transparent !important",
			borderRadius: "0 !important",
			boxShadow: "none !important",
			fontSize: "12px !important",
			fontWeight: "750 !important",
			lineHeight: "1.25 !important",
			transform: "none !important",
		},
	},
	paper: {
		background: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
		backgroundColor: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
		backgroundImage: "none !important",
		color: theme.palette.text.primary,
		border: `1px solid ${theme.palette.mode === "light" ? "rgba(32, 50, 64, 0.12)" : "rgba(255, 255, 255, 0.14)"}`,
		borderRadius: "14px !important",
		boxShadow: theme.palette.mode === "light"
			? "0 18px 42px rgba(32, 50, 64, 0.18)"
			: "0 18px 42px rgba(0, 0, 0, 0.46)",
		backdropFilter: "none !important",
		WebkitBackdropFilter: "none !important",
		marginTop: 4,
	},
	popper: {
		zIndex: `${theme.zIndex.modal + 2} !important`,
		background: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
		backgroundColor: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
		backgroundImage: "none !important",
		borderRadius: "14px !important",
		backdropFilter: "none !important",
		WebkitBackdropFilter: "none !important",
		opacity: "1 !important",
		"& .MuiAutocomplete-paper, & .MuiPaper-root, & .MuiAutocomplete-listbox": {
			background: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
			backgroundColor: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
			backgroundImage: "none !important",
			backdropFilter: "none !important",
			WebkitBackdropFilter: "none !important",
			opacity: "1 !important",
		},
	},
	listbox: {
		maxHeight: 320,
		padding: theme.spacing(0.75),
		display: "grid",
		gap: theme.spacing(0.35),
		background: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
		backgroundColor: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
		backgroundImage: "none !important",
		backdropFilter: "none !important",
		WebkitBackdropFilter: "none !important",
		"& .MuiAutocomplete-option": {
			borderRadius: 10,
			color: theme.palette.text.primary,
			minHeight: "34px !important",
			padding: theme.spacing(0.7, 1.2),
		},
		"& .MuiAutocomplete-option[aria-selected='true']": {
			backgroundColor: theme.palette.mode === "light" ? "rgba(26, 115, 232, 0.1)" : "rgba(129, 212, 250, 0.14)",
		},
		"& .MuiAutocomplete-option.Mui-focused": {
			backgroundColor: theme.palette.mode === "light" ? "rgba(26, 115, 232, 0.08)" : "rgba(129, 212, 250, 0.1)",
		},
	},
}));

const getSolidBackground = (theme) => theme.palette.mode === "light" ? "#fff" : "#18202c";

const getOptionValue = (option) => {
	if (option === null || option === undefined) return option;
	if (typeof option === "object" && Object.prototype.hasOwnProperty.call(option, "value")) {
		return option.value;
	}
	return option;
};

const getOptionLabel = (option) => {
	if (option === null || option === undefined) return "";
	if (typeof option === "object") {
		return option.label ?? option.value ?? "";
	}
	return String(option);
};

const getDefaultOptionSearchText = (option) => {
	if (option === null || option === undefined) return "";
	if (typeof option === "object") {
		return [option.label, option.value, option.searchText].filter(Boolean).join(" ");
	}
	return String(option);
};

const normalizeFilterText = (value) => String(value || "")
	.normalize("NFD")
	.replace(/[\u0300-\u036f]/g, "")
	.toLowerCase()
	.replace(/[^\p{L}\p{N}]+/gu, " ")
	.trim();

const getLiveInputValue = (name) => {
	if (!name || typeof document === "undefined") return null;
	const activeElement = document.activeElement;
	return activeElement?.dataset?.atlAutocompleteName === name ? activeElement.value || "" : null;
};

const findOption = (options, value) => {
	if (value === null || value === undefined || value === "") {
		return null;
	}
	return options.find((option) => getOptionValue(option) === value) || null;
};

const findOptions = (options, value) => {
	if (!Array.isArray(value)) {
		return [];
	}
	return value.map((item) => findOption(options, item)).filter(Boolean);
};

const AutocompleteSelect = ({
	autoFocus = false,
	className,
	control,
	defaultValue = null,
	disabled = false,
	disableClearable = false,
	emptyValue = null,
	error,
	filterOptions,
	fullWidth = true,
	getOptionSearchText = getDefaultOptionSearchText,
	getOptionDisabled,
	helperText,
	label,
	multiple = false,
	name,
	onChange,
	optionLimit = 80,
	options = [],
	placeholder,
	renderTags,
	renderOptionContent,
	renderSelectedAdornment,
	rules,
	size = "medium",
	value,
	variant = "outlined",
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const solidBackground = getSolidBackground(theme);
	const solidInputStyle = useMemo(() => ({
		background: solidBackground,
		backgroundColor: solidBackground,
		backgroundImage: "none",
		backgroundClip: "padding-box",
		backdropFilter: "none",
		WebkitBackdropFilter: "none",
	}), [solidBackground]);
	const resolvedFilterOptions = useMemo(() => {
		if (filterOptions) {
			return filterOptions;
		}

		return (availableOptions, state) => {
			const liveInputValue = getLiveInputValue(name);
			const normalizedInput = normalizeFilterText(liveInputValue !== null ? liveInputValue : state.inputValue);
			if (!normalizedInput) {
				return optionLimit ? availableOptions.slice(0, optionLimit) : availableOptions;
			}

			const tokens = normalizedInput.split(/\s+/);
			const filteredOptions = availableOptions
				.map((option) => {
					const normalizedLabel = normalizeFilterText(getOptionLabel(option));
					const normalizedValue = normalizeFilterText(getOptionValue(option));
					const normalizedOption = normalizeFilterText(getOptionSearchText(option));
					const matches = tokens.every((token) => normalizedOption.includes(token));
					if (!matches) return null;

					const score = normalizedLabel.startsWith(normalizedInput)
						? 0
						: tokens.every((token) => normalizedLabel.includes(token))
							? 1
							: normalizedValue.includes(normalizedInput)
								? 2
								: 3;

					return {option, score, label: normalizedLabel};
				})
				.filter(Boolean)
				.sort((first, second) => {
					if (first.score !== second.score) {
						return first.score - second.score;
					}
					return first.label.localeCompare(second.label);
				});

			const resolvedOptions = filteredOptions.map(({option}) => option);
			return optionLimit ? resolvedOptions.slice(0, optionLimit) : resolvedOptions;
		};
	}, [filterOptions, getOptionSearchText, name, optionLimit]);
	const helperTextStyle = useMemo(() => ({
		position: "static",
		margin: `${theme.spacing(0.45)} 0 0 ${theme.spacing(1.25)}`,
		padding: 0,
		color: theme.palette.error.main,
		background: "transparent",
		borderRadius: 0,
		boxShadow: "none",
		fontSize: 12,
		fontWeight: 750,
		lineHeight: 1.25,
		transform: "none",
	}), [theme]);
	const slotProps = useMemo(() => ({
		paper: {
			className: classes.paper,
			elevation: 8,
			style: solidInputStyle,
		},
		popper: {
			className: classes.popper,
		},
		listbox: {
			className: classes.listbox,
			style: solidInputStyle,
		},
	}), [classes.listbox, classes.paper, classes.popper, solidInputStyle]);

	const renderSelect = (selectedValue, handleChange) => {
		const selectedOption = multiple ? findOptions(options, selectedValue) : findOption(options, selectedValue);
		const selectedAdornment = !multiple ? renderSelectedAdornment?.(selectedOption) : null;

		return (
		<Autocomplete
			className={clsx(classes.root, className)}
			data-solid-autocomplete="true"
			disabled={disabled}
			disableClearable={disableClearable}
			filterOptions={resolvedFilterOptions}
			filterSelectedOptions={multiple}
			fullWidth={fullWidth}
			getOptionDisabled={getOptionDisabled}
			getOptionLabel={getOptionLabel}
			isOptionEqualToValue={(option, selectedOption) => getOptionValue(option) === getOptionValue(selectedOption)}
			onChange={(event, newSelectedOption) => {
				const nextValue = multiple
					? (newSelectedOption || []).map((option) => getOptionValue(option))
					: newSelectedOption ? getOptionValue(newSelectedOption) : emptyValue;
				if (handleChange) {
					handleChange(nextValue);
				} else {
					onChange?.(nextValue);
				}
			}}
			multiple={multiple}
			options={options}
			renderOption={renderOptionContent ? (props, option) => (
				<li {...props}>
					{renderOptionContent(option)}
				</li>
			) : undefined}
			renderValue={renderTags ? (selectedOptions, getItemProps) => (
				renderTags(selectedOptions, ({index}) => getItemProps({index}))
			) : undefined}
			size={size}
			slots={{paper: Paper}}
			slotProps={slotProps}
			value={selectedOption}
			renderInput={(params) => {
				const {slotProps: paramsSlotProps = {}, ...textFieldParams} = params;
				return (
					<TextField
						{...textFieldParams}
						autoFocus={autoFocus}
						error={Boolean(error)}
						helperText={helperText}
						label={label}
						placeholder={placeholder}
						size={size}
						slotProps={{
							...paramsSlotProps,
							input: {
								...paramsSlotProps.input,
								startAdornment: selectedAdornment ? (
									<React.Fragment>
										{selectedAdornment}
										{paramsSlotProps.input?.startAdornment}
									</React.Fragment>
								) : paramsSlotProps.input?.startAdornment,
								style: {
									...paramsSlotProps.input?.style,
									...solidInputStyle,
								},
							},
							htmlInput: {
								...paramsSlotProps.htmlInput,
								"data-atl-autocomplete-name": name,
								style: {
									...paramsSlotProps.htmlInput?.style,
									background: "transparent",
									backgroundColor: "transparent",
								},
							},
							formHelperText: {
								...paramsSlotProps.formHelperText,
								style: {
									...helperTextStyle,
									...paramsSlotProps.formHelperText?.style,
								},
							},
						}}
						variant={variant}
					/>
				);
			}}
		/>
		);
	};

	if (control && name) {
		return (
			<Controller
				name={name}
				control={control}
				defaultValue={defaultValue}
				rules={rules}
				render={({onChange: controllerOnChange, value: controllerValue}) => (
					renderSelect(controllerValue, controllerOnChange)
				)}
			/>
		);
	}

	return renderSelect(value, onChange);
};

export default React.memo(AutocompleteSelect);
